import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { beginPayment } from '@/api/payments';
import { LoadingScreen } from '@/features/check/StatusScreen';
import { PaymentFailureScreen } from '@/features/payment/PaymentFailureScreen';
import { PaymentRunner } from '@/features/payment/PaymentRunner';
import { clearDraft, loadDraft, saveDraft } from '@/payment/draft';
import { useCheckout } from './checkoutContext';
import { HttpError } from '@/api/client';
import type { PaymentFailure, PaymentResult } from '@/api/types';

const UNREACHABLE: PaymentFailure = { code: 'gatewayUnavailable', retryable: true };

/**
 * Where the money starts moving.
 *
 * `useQuery` rather than `useMutation`, despite this being a POST: a mutation fired from an effect needs a ref
 * guard against React's development double-mount, and this app already got that wrong once on the session
 * route — the guard won the race and the screen sat on a spinner forever.
 *
 * The request is NOT safe to repeat, which is why `retry` is off. There is no idempotency key: the server
 * mints a payment id per call, so a second attempt starts a second payment. That is not a double charge — only
 * one of them can ever be completed, and an unused one is never charged — but it is not something to do on
 * purpose while a guest waits. A failure sends them to the failure screen, which starts a clean attempt.
 */
export function PayRoute() {
    const { location, check, accessToken } = useCheckout();
    const navigate = useNavigate();

    const draft = useMemo(() => loadDraft(check.id), [check.id]);

    const begin = useQuery({
        queryKey: ['beginPayment', check.id, draft?.method, draft?.tipCents],
        queryFn: async ({ signal }) => {
            const started = await beginPayment(
                accessToken,
                {
                    method: draft!.method!,
                    tipCents: draft!.tipCents,

                    /*
                     * What is still DUE plus the tip, not the check total plus the tip. Those are the same
                     * number until somebody part-pays, and different afterwards — and the server compares
                     * against what is due, so using the total would refuse every check a friend had chipped
                     * in on.
                     */
                    expectedTotalCents: check.amountDueCents + draft!.tipCents,
                },
                signal,
            );

            // Written down before the action is obeyed: a redirect may destroy this page microseconds later,
            // and the return route has nothing but the draft to work from.
            saveDraft({ ...draft!, paymentId: started.paymentId });

            return started;
        },
        enabled: !!draft?.method,
        retry: false,
        staleTime: Infinity,
    });

    /*
     * No method recorded means they never chose one — a typed URL, or storage cleared mid-flow. The tip step
     * is where the flow decides which methods exist, so it is the honest place to send them back to.
     */
    if (!draft?.method) return <Navigate to="/tip" replace />;

    const retry = () => {
        // A clean attempt from the tip step: a new payment, priced against the check as it stands now.
        clearDraft();
        navigate('/tip');
    };

    if (begin.isPending) return <LoadingScreen />;

    if (begin.isError) {
        return (
            <PaymentFailureScreen
                location={location}
                check={check}
                failure={failureFrom(begin.error)}
                onRetry={retry}
                onBackToCheck={() => navigate('/check')}
            />
        );
    }

    return (
        <PaymentRunner
            location={location}
            check={check}
            accessToken={accessToken}
            paymentId={begin.data.paymentId}
            initial={{ kind: 'action', action: begin.data.next }}
            onRetry={retry}
            onBackToCheck={() => navigate('/check')}
        />
    );
}

/**
 * What went wrong, when the server said.
 *
 * Almost everything here is a network or a gateway problem and the guest gets the same words either way. The
 * exception is the check having moved under them — a friend covered a share, or a server added a round — which
 * is not a fault at all and is the one case where "try again" would take them straight back to the same
 * refusal. The server answers 409 with the code; anything else is treated as the gateway being unreachable.
 */
function failureFrom(error: unknown): PaymentFailure {
    if (!(error instanceof HttpError) || error.status !== 409) return UNREACHABLE;

    const body = error.body as PaymentResult | null;

    return body?.failure ?? UNREACHABLE;
}
